import { isDev } from "@certa/common/utils/isDev";
import { getStudioProcessDesignerRoute } from "@certa/common/utils/routes";
import { navigateByPush, navigateByReplace } from "@certa/routing/routes";
import { ProcessLayoutMode } from "@certa/studio-core/src/types/process.types";

export type StudioQueryParams =
  | "view"
  | "subView"
  | "focusField"
  | "nodeId"
  | "componentId"
  | "ruleId"
  | "actionId"
  | "eventId"
  | "objectTag"
  | "objectTab"
  | "workflow-view" // Record's workflow view; value: kind_tag
  | "controls" // comma separated list of controls to show
  | "step" // value is number
  | "stepgroup" // value is number
  | "rightPanelCollapsed"; // value is boolean string

// Simple Query Parameter Manager
const createQueryParamsManager = () => {
  const setParams = (
    paramsMap: Partial<Record<StudioQueryParams, string>>,
    options: { clearAll?: boolean; removeParams?: StudioQueryParams[] } = {}
  ) => {
    let params = new URLSearchParams(window.location.search);

    // Clear all if requested
    if (options.clearAll) {
      params = new URLSearchParams();
    }

    // Remove specific params if requested
    if (options.removeParams) {
      options.removeParams.forEach(param => params.delete(param));
    }

    // Set new params
    Object.entries(paramsMap).forEach(([key, value]) => {
      if (value === "" || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, encodeURIComponent(value));
      }
    });

    return params.toString();
  };

  const getCurrentParams = () => {
    return new URLSearchParams(window.location.search);
  };

  const getParam = (param: StudioQueryParams) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
  };

  return {
    setParams,
    getCurrentParams,
    getParam
  };
};

// Create a singleton instance
const queryManager = createQueryParamsManager();

const INVALID_NAVIGATION_VERBIAGES = {
  error: "Invalid navigation! Navigation attempt to same url",
  warn: "Warning: Navigation attempts to same url"
};

const studioNavigate = (
  queryString: string,
  method: "push" | "replace" = "push"
) => {
  const currentWindowQueryString = window.location.search.substring(1);
  const isSameQueryString = queryString === currentWindowQueryString;

  if (isDev) {
    if (isSameQueryString) {
      if (method === "push") {
        // eslint-disable-next-line no-console
        console.error(INVALID_NAVIGATION_VERBIAGES.error);
      } else if (method === "replace") {
        // eslint-disable-next-line no-console
        console.warn(INVALID_NAVIGATION_VERBIAGES.warn);
      }
    } else if (method === "push") {
      navigateByPush(`?${queryString}`);
    } else if (method === "replace") {
      navigateByReplace(`?${queryString}`);
    }
  } else {
    if (isSameQueryString) {
      return; // Silent return for non-dev environment.
    } else if (method === "push") {
      navigateByPush(`?${queryString}`);
    } else if (method === "replace") {
      navigateByReplace(`?${queryString}`);
    }
  }
};

const navigateToUrl = (url: string, method: "push" | "replace" = "push") => {
  if (method === "push") {
    navigateByPush(url);
  } else {
    navigateByReplace(url);
  }
};

const studioDesignerURLRoutes = {
  setSelectedComponent: ({
    nodeId,
    componentId
  }: {
    nodeId: string;
    componentId: string;
    subview?:
      | "properties"
      | "rules"
      | "access"
      | "activityLogs"
      | "appSettings_appSettingtabName"
      | "";
  }) => {
    const queryString = queryManager.setParams({
      nodeId,
      componentId
    });
    studioNavigate(queryString);
  },

  switchCanvasTab: ({
    view,
    extraParams = {},
    clearOtherParams = true
  }: {
    view: ProcessLayoutMode;
    extraParams?: Partial<Record<StudioQueryParams, string>>;
    clearOtherParams?: boolean;
  }) => {
    if (Object.values(ProcessLayoutMode).includes(view)) {
      const queryString = queryManager.setParams(
        { view, ...extraParams },
        { clearAll: clearOtherParams }
      );
      studioNavigate(queryString);
    }
  },

  setRuleId: (ruleId: string) => {
    const queryString = queryManager.setParams({ ruleId });
    studioNavigate(queryString);
  },

  setActionId: (actionId: string) => {
    const queryString = queryManager.setParams({ actionId });
    studioNavigate(queryString);
  },

  setEventId: (eventId: string) => {
    const queryString = queryManager.setParams(
      {
        view: ProcessLayoutMode.RULES_VIEW,
        eventId
      },
      { clearAll: true }
    );

    if (eventId === "") {
      studioNavigate(queryString, "replace");
    } else {
      studioNavigate(queryString);
    }
  },

  setStep: (step: number) => {
    const queryString = queryManager.setParams({ step: `${step}` });
    studioNavigate(queryString);
  },

  setStepGroup: (stepGroup: number) => {
    const queryString = queryManager.setParams({ stepgroup: `${stepGroup}` });
    studioNavigate(queryString);
  },

  setControls: (controls: string) => {
    const queryString = queryManager.setParams({ controls });
    studioNavigate(queryString);
  },

  onFlowModeCanvasClick: ({
    targetView
  }: {
    targetView: ProcessLayoutMode;
  }) => {
    const queryString = queryManager.setParams(
      { view: targetView },
      { clearAll: true }
    );
    studioNavigate(queryString);
  },

  onFlowModeStepClick: (
    stepId: number,
    swimlaneId: number,
    targetView: ProcessLayoutMode
  ) => {
    const queryString = queryManager.setParams({
      view: targetView,
      step: `${stepId}`,
      stepgroup: `${swimlaneId}`
    });
    studioNavigate(queryString);
  },

  onFlowModeSwimlaneClick: (
    swimlaneId: number,
    targetView: ProcessLayoutMode
  ) => {
    const queryString = queryManager.setParams(
      {
        view: targetView,
        stepgroup: `${swimlaneId}`
      },
      { removeParams: ["step"] }
    );
    studioNavigate(queryString);
  },

  onPreviewModeStepClick: (stepId: number, swimlaneId: number) => {
    const queryString = queryManager.setParams({
      view: ProcessLayoutMode.PREVIEW_VIEW,
      step: `${stepId}`,
      stepgroup: `${swimlaneId}`
    });
    studioNavigate(queryString);
  },

  setDataObjectTag: (objectTag: string) => {
    const queryString = queryManager.setParams(
      { objectTag },
      { removeParams: ["objectTab", "workflow-view"] }
    );
    studioNavigate(queryString);
  },

  setObjectTab: (objectTab: string) => {
    const queryString = queryManager.setParams({ objectTab });
    studioNavigate(queryString);
  },

  onRecordWorkflowViewChange: (kindTag: string) => {
    const queryString = queryManager.setParams({ "workflow-view": kindTag });
    studioNavigate(queryString);
  },

  onFlowModeStepClickCrossProcess: ({
    appId,
    branchId,
    savepointId,
    targetProcessId,
    targetProcessVersionId,
    targetView,
    swimlaneId,
    stepId
  }: {
    appId: number;
    branchId: number;
    savepointId: number;
    targetProcessId: number;
    targetProcessVersionId: number;
    targetView: ProcessLayoutMode;
    swimlaneId: number;
    stepId: number;
  }) => {
    const url = getStudioProcessDesignerRoute({
      id: appId,
      branch: branchId,
      savepoint: savepointId,
      process: targetProcessId,
      processSavepointId: targetProcessVersionId
    });
    const queryParams = new URLSearchParams();
    queryParams.set("view", targetView);
    queryParams.set("step", `${stepId}`);
    queryParams.set("stepgroup", `${swimlaneId}`);
    navigateToUrl(`${url}?${queryParams.toString()}`);
  },

  onFlowModeSwimlaneClickCrossProcess: ({
    appId,
    branchId,
    savepointId,
    targetProcessId,
    targetProcessVersionId,
    targetView,
    swimlaneId
  }: {
    appId: number;
    branchId: number;
    savepointId: number;
    targetProcessId: number;
    targetProcessVersionId: number;
    targetView: ProcessLayoutMode;
    swimlaneId: number;
  }) => {
    const url = getStudioProcessDesignerRoute({
      id: appId,
      branch: branchId,
      savepoint: savepointId,
      process: targetProcessId,
      processSavepointId: targetProcessVersionId
    });
    const queryParams = new URLSearchParams();
    queryParams.set("view", targetView);
    queryParams.set("stepgroup", `${swimlaneId}`);
    navigateToUrl(`${url}?${queryParams.toString()}`);
  },

  setRightPanelCollapsed: (isCollapsed: boolean) => {
    const queryString = isCollapsed
      ? queryManager.setParams({}, { removeParams: ["rightPanelCollapsed"] })
      : queryManager.setParams({ rightPanelCollapsed: "false" });
    studioNavigate(queryString, "replace");
  },

  // Utility methods
  getCurrentQueryParams: () => queryManager.getCurrentParams(),
  getParam: (param: StudioQueryParams) => queryManager.getParam(param)
};

export { studioDesignerURLRoutes };
